import { faEdit, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  FranchiseFeePayout,
  PayoutAdjustment,
  createFranchiseFeePayoutAdjustment,
  deleteFranchiseFeePayoutAdjustment,
  updateFranchiseFeePayoutAdjustment,
} from 'src/models/hq/franchise-fee-payout';
import { canModifyPayout } from 'src/models/hq/franchise-fee-payout/utils';
import { useHQPermissionCheck } from 'src/shared/HQ/useHQPermissionCheck';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

import { AdjustmentDialog } from './AdjustmentDialog';


interface PayoutAdjustmentsListProps {
  payout: FranchiseFeePayout;
}

export function PayoutAdjustmentsList({ payout }: PayoutAdjustmentsListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.payouts',
  });
  const queryClient = useQueryClient();
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();
  const { canEditHQFranchiseFees } = useHQPermissionCheck();

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAdjustment, setEditingAdjustment] =
    useState<PayoutAdjustment | null>(null);

  const adjustments = payout.payoutAdjustments || [];
  const canModify =
    canModifyPayout(payout.payoutStatus) && canEditHQFranchiseFees();

  // Mutations
  const createMutation = useMutation({
    mutationFn: ({ amount, reason }: { amount: number; reason: string }) =>
      createFranchiseFeePayoutAdjustment(payout.id.toString(), {
        amount,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchise-fee-payout', payout.id.toString()],
      });
      toast.success(t('adjustmentCreatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('failedToCreateAdjustment'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      adjustmentId,
      amount,
      reason,
    }: {
      adjustmentId: string;
      amount: number;
      reason: string;
    }) =>
      updateFranchiseFeePayoutAdjustment(payout.id.toString(), adjustmentId, {
        amount,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchise-fee-payout', payout.id.toString()],
      });
      toast.success(t('adjustmentUpdatedSuccessfully'));
    },
    onError: () => {
      toast.error(t('failedToUpdateAdjustment'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (adjustmentId: string) =>
      deleteFranchiseFeePayoutAdjustment(payout.id.toString(), adjustmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['franchise-fee-payout', payout.id.toString()],
      });
      toast.success(t('adjustmentDeletedSuccessfully'));
    },
    onError: () => {
      toast.error(t('failedToDeleteAdjustment'));
    },
  });

  // Handlers
  const handleOpenAddDialog = () => {
    setEditingAdjustment(null);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (adjustment: PayoutAdjustment) => {
    setEditingAdjustment(adjustment);
    setIsDialogOpen(true);
  };

  const handleSubmitAdjustment = async (
    amount: number,
    reason: string,
    adjustmentId?: string
  ) => {
    if (adjustmentId) {
      // Editing existing adjustment
      await updateMutation.mutateAsync({ adjustmentId, amount, reason });
    } else {
      // Creating new adjustment
      await createMutation.mutateAsync({ amount, reason });
    }
  };

  const handleDeleteAdjustment = async (adjustment: PayoutAdjustment) => {
    const confirmed = await confirm({
      title: t('confirmDeleteAdjustment'),
      message: t('confirmDeleteAdjustmentMessage', {
        reason: adjustment.reason,
        amount: Math.abs(adjustment.amountInDollars),
      }),
    });

    if (confirmed) {
      await deleteMutation.mutateAsync(adjustment.id.toString());
    }
  };

  // Enhanced line items with action buttons
  const adjustmentLineItems = adjustments.map((adjustment) => ({
    id: adjustment.id.toString(),
    label: adjustment.reason,
    amount: adjustment.amountInDollars,
    showSign: true,
    actions: canModify ? (
      <Stack direction="row">
        <IconButton
          size="small"
          onClick={() => handleOpenEditDialog(adjustment)}
          title={t('editAdjustment')}
        >
          <Icon icon={faEdit} size="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDeleteAdjustment(adjustment)}
          title={t('deleteAdjustment')}
          color="error"
        >
          <Icon icon={faTrash} size="small" />
        </IconButton>
      </Stack>
    ) : undefined,
  }));

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t('Adjustments')}
          </Typography>
          {canModify && (
            <Button
              startIcon={
                <Icon icon={faPlus} />
              }
              onClick={handleOpenAddDialog}
              size="small"
              variant="outlined"
            >
              {t('addAdjustment')}
            </Button>
          )}
        </Stack>

        {/* Adjustments List */}
        {adjustmentLineItems.length > 0 ? (
          <Stack spacing={1}>
            {adjustments.map((adjustment) => {
              const item = adjustmentLineItems.find(
                (i) => i.id === adjustment.id.toString()
              );
              return (
                <Stack
                  key={adjustment.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ py: 0.5 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{item?.label}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {adjustment.createdByName} •{' '}
                      {formatFullDateAndTime(adjustment.updatedAt)}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="body2"
                      color={
                        (item?.amount || 0) < 0 ? 'error.main' : 'success.main'
                      }
                      fontWeight={500}
                    >
                      {(item?.amount || 0) < 0 ? '-' : '+'}
                      {formatCurrency(Math.abs(item?.amount || 0))}
                    </Typography>
                    {item?.actions}
                  </Stack>
                </Stack>
              );
            })}

            {/* Total */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
            >
              <Typography variant="body2" fontWeight={600}>
                {t('totalAdjustments')}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={
                  (payout.adjustmentsTotalInDollars || 0) < 0
                    ? 'error.main'
                    : 'success.main'
                }
              >
                {(payout.adjustmentsTotalInDollars || 0) < 0 ? '-' : '+'}
                {formatCurrency(
                  Math.abs(payout.adjustmentsTotalInDollars || 0)
                )}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: 'center' }}
          >
            {t('No adjustments have been made to this payout.')}
          </Typography>
        )}

        {!canModify && adjustments.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block' }}
          >
            {t('adjustmentsCannotBeModified')}
          </Typography>
        )}
      </Box>
      {/* Dialogs */}
      <AdjustmentDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingAdjustment(null);
        }}
        onSubmit={handleSubmitAdjustment}
        adjustment={editingAdjustment}
        currentAmountOwed={payout.amountOwedInDollars || 0}
      />
      <ConfirmDialog {...confirmDialogProps} />
    </>
  );
}
